import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, ArrowUp, ArrowDown,
  Loader2, Check, Power, PowerOff, MapPin,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import adminApi from '../lib/adminApi';

const UF_NOMES = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia',
  CE: 'Ceará', DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás',
  MA: 'Maranhão', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais',
  PA: 'Pará', PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí',
  RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul',
  RO: 'Rondônia', RR: 'Roraima', SC: 'Santa Catarina', SP: 'São Paulo',
  SE: 'Sergipe', TO: 'Tocantins',
};

function CidadeForm({ inicial, onSalvar, onCancelar, salvando }) {
  const [form, setForm] = useState(inicial || { nome: '', uf: '', estado: '', ativa: true });

  const handleUf = (uf) => {
    const upper = uf.toUpperCase().slice(0, 2);
    setForm({ ...form, uf: upper, estado: UF_NOMES[upper] || form.estado });
  };

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSalvar(form); }}
      className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4 mb-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Nome da cidade</label>
          <input
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
            required data-testid="cidade-nome-input"
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]"
            placeholder="Ex: São Paulo"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">UF</label>
          <input
            value={form.uf}
            onChange={(e) => handleUf(e.target.value)}
            required maxLength={2} data-testid="cidade-uf-input"
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#1D9E75]"
            placeholder="SP"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Estado</label>
        <input
          value={form.estado}
          onChange={(e) => setForm({ ...form, estado: e.target.value })}
          required data-testid="cidade-estado-input"
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]"
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.ativa}
          onChange={(e) => setForm({ ...form, ativa: e.target.checked })}
          data-testid="cidade-ativa-input"
          className="w-4 h-4 accent-[#1D9E75]"
        />
        <span className="text-sm text-gray-700">Cidade ativa (visível no site)</span>
      </label>

      <div className="flex gap-2 pt-1">
        <button
          type="submit" disabled={salvando} data-testid="salvar-cidade-btn"
          className="flex-1 bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl py-2 text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Salvar
        </button>
        <button
          type="button" onClick={onCancelar}
          className="px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl py-2 text-sm transition-colors"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

export default function AdminCidades() {
  const [cidades, setCidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get('/admin/cidades');
      setCidades(res.data);
    } catch (err) {
      // handled by layout
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleCriar = async (form) => {
    setSalvando(true);
    try {
      await adminApi.post('/admin/cidades', form);
      setCriando(false);
      carregar();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Erro ao criar cidade');
    } finally { setSalvando(false); }
  };

  const handleAtualizar = async (form) => {
    setSalvando(true);
    try {
      await adminApi.patch(`/admin/cidades/${editando.id}`, form);
      setEditando(null);
      carregar();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Erro ao atualizar');
    } finally { setSalvando(false); }
  };

  const handleToggle = async (c) => {
    await adminApi.patch(`/admin/cidades/${c.id}`, { ativa: !c.ativa });
    carregar();
  };

  const handleExcluir = async (c) => {
    if (!window.confirm(`Excluir "${c.nome}/${c.uf}"?`)) return;
    await adminApi.delete(`/admin/cidades/${c.id}`);
    carregar();
  };

  const handleMover = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= cidades.length) return;
    const lista = [...cidades];
    [lista[i], lista[j]] = [lista[j], lista[i]];
    setCidades(lista);
    await adminApi.post('/admin/cidades/reordenar', lista.map((c, k) => ({ id: c.id, ordem: k + 1 })));
  };

  return (
    <AdminLayout title="Cidades">
      {!criando && !editando && (
        <button
          onClick={() => setCriando(true)} data-testid="nova-cidade-btn"
          className="mb-4 bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl px-4 py-2.5 text-sm transition-colors flex items-center gap-2"
        >
          <Plus size={15} />
          Nova cidade
        </button>
      )}

      {criando && <CidadeForm onSalvar={handleCriar} onCancelar={() => setCriando(false)} salvando={salvando} />}
      {editando && <CidadeForm inicial={editando} onSalvar={handleAtualizar} onCancelar={() => setEditando(null)} salvando={salvando} />}

      {loading ? (
        <div className="py-12 text-center text-gray-400 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Carregando...
        </div>
      ) : cidades.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
          <MapPin size={28} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Nenhuma cidade cadastrada.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
          {cidades.map((c, idx) => (
            <div key={c.id} data-testid={`cidade-row-${c.uf.toLowerCase()}`} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${c.ativa ? 'bg-[#E1F5EE]' : 'bg-gray-100'}`}>
                <span className={`font-bold text-xs ${c.ativa ? 'text-[#1D9E75]' : 'text-gray-400'}`}>{c.uf}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${c.ativa ? 'text-gray-900' : 'text-gray-400'}`}>{c.nome}</p>
                <p className="text-xs text-gray-500 truncate">{c.estado}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handleMover(idx, -1)} disabled={idx === 0} className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-30 text-gray-500"><ArrowUp size={14} /></button>
                <button onClick={() => handleMover(idx, 1)} disabled={idx === cidades.length - 1} className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-30 text-gray-500"><ArrowDown size={14} /></button>
                <button onClick={() => handleToggle(c)} data-testid={`toggle-${c.uf.toLowerCase()}`} className={`p-1.5 rounded-lg ${c.ativa ? 'text-[#1D9E75] hover:bg-[#E1F5EE]' : 'text-gray-400 hover:bg-gray-100'}`}>
                  {c.ativa ? <Power size={14} /> : <PowerOff size={14} />}
                </button>
                <button onClick={() => setEditando(c)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Pencil size={14} /></button>
                <button onClick={() => handleExcluir(c)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
